import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Minus, Link2, Trash2, MousePointer2 } from "lucide-react";
import {
  useCanvas,
  useCanvasActions,
  type CanvasNote,
} from "../api/projects";

const COLORS = ["#fde68a", "#bbf7d0", "#bfdbfe", "#fbcfe8", "#ddd6fe", "#fecaca", "#e2e8f0"];

type View = { x: number; y: number; scale: number };

export function LienzoCanvas({ projectId }: { projectId: number }) {
  const { data, isLoading } = useCanvas(projectId);
  const actions = useCanvasActions(projectId);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [view, setView] = useState<View>({ x: 40, y: 40, scale: 1 });
  const [local, setLocal] = useState<Record<number, { x: number; y: number }>>({});
  const [connectMode, setConnectMode] = useState(false);
  const [connectFrom, setConnectFrom] = useState<number | null>(null);

  const notas = (data?.notas ?? []).map((n) => (local[n.id] ? { ...n, ...local[n.id] } : n));
  const conexiones = data?.conexiones ?? [];
  const byId = (id: number) => notas.find((n) => n.id === id);

  // Pantalla → coordenadas del lienzo
  const toCanvas = (clientX: number, clientY: number) => {
    const r = wrapRef.current!.getBoundingClientRect();
    return {
      x: Math.round((clientX - r.left - view.x) / view.scale),
      y: Math.round((clientY - r.top - view.y) / view.scale),
    };
  };

  // ── Pan (arrastrar fondo)
  function startPan(e: React.MouseEvent) {
    if (e.button !== 0) return;
    const sx = e.clientX, sy = e.clientY, vx = view.x, vy = view.y;
    const move = (ev: MouseEvent) => setView((v) => ({ ...v, x: vx + (ev.clientX - sx), y: vy + (ev.clientY - sy) }));
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  // ── Zoom (rueda, anclado al cursor)
  function onWheel(e: React.WheelEvent) {
    e.preventDefault();
    const r = wrapRef.current!.getBoundingClientRect();
    const mx = e.clientX - r.left, my = e.clientY - r.top;
    setView((v) => {
      const scale = Math.min(2.5, Math.max(0.3, v.scale * (e.deltaY < 0 ? 1.1 : 0.9)));
      const cx = (mx - v.x) / v.scale, cy = (my - v.y) / v.scale;
      return { scale, x: mx - cx * scale, y: my - cy * scale };
    });
  }

  // ── Arrastrar nota
  function startDrag(e: React.MouseEvent, note: CanvasNote) {
    e.stopPropagation();
    if (connectMode) { onConnectClick(note.id); return; }
    const sx = e.clientX, sy = e.clientY, ox = note.x, oy = note.y;
    const move = (ev: MouseEvent) => setLocal((l) => ({ ...l, [note.id]: { x: Math.round(ox + (ev.clientX - sx) / view.scale), y: Math.round(oy + (ev.clientY - sy) / view.scale) } }));
    const up = (ev: MouseEvent) => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      const fx = Math.round(ox + (ev.clientX - sx) / view.scale), fy = Math.round(oy + (ev.clientY - sy) / view.scale);
      if (fx !== ox || fy !== oy) actions.updateNote.mutate({ id: note.id, payload: { x: fx, y: fy } });
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  }

  function onConnectClick(noteId: number) {
    if (connectFrom == null) { setConnectFrom(noteId); return; }
    if (connectFrom !== noteId) actions.addConnection.mutate({ desde_nota_id: connectFrom, hasta_nota_id: noteId });
    setConnectFrom(null);
    setConnectMode(false);
  }

  function addNoteAt(e: React.MouseEvent) {
    const { x, y } = toCanvas(e.clientX, e.clientY);
    actions.addNote.mutate({ x: x - 90, y: y - 60, color: COLORS[0] });
  }

  useEffect(() => { setLocal({}); }, [data]);

  if (isLoading) return <div className="flex items-center gap-2 text-slate-500"><Loader2 className="animate-spin" size={18} /> Cargando lienzo…</div>;

  return (
    <div className="relative rounded-2xl border border-slate-200 overflow-hidden bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:20px_20px]" style={{ height: "72vh" }}>
      {/* Barra de herramientas */}
      <div className="absolute z-20 top-3 left-3 flex items-center gap-1.5 rounded-xl bg-white/95 border border-slate-200 shadow-sm px-2 py-1.5">
        <button onClick={() => setView((v) => ({ ...v, scale: Math.min(2.5, v.scale * 1.15) }))} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Acercar"><Plus size={15} /></button>
        <span className="text-xs font-bold text-slate-500 w-10 text-center">{Math.round(view.scale * 100)}%</span>
        <button onClick={() => setView((v) => ({ ...v, scale: Math.max(0.3, v.scale * 0.87) }))} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Alejar"><Minus size={15} /></button>
        <button onClick={() => setView({ x: 40, y: 40, scale: 1 })} className="p-1.5 rounded hover:bg-slate-100 text-slate-600 text-xs font-bold" title="Restablecer vista"><MousePointer2 size={15} /></button>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <button
          onClick={() => { setConnectMode((c) => !c); setConnectFrom(null); }}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${connectMode ? "bg-brand-700 text-white" : "hover:bg-slate-100 text-slate-600"}`}
          title="Conectar notas"
        >
          <Link2 size={14} /> Conectar
        </button>
      </div>

      <div className="absolute z-20 top-3 right-3 rounded-xl bg-white/95 border border-slate-200 shadow-sm px-3 py-1.5 text-xs font-semibold text-slate-500">
        {connectMode ? (connectFrom ? "Elige la nota destino…" : "Elige la nota origen…") : "Doble clic para crear nota · rueda para zoom · arrastra el fondo para mover"}
      </div>

      {/* Lienzo */}
      <div
        ref={wrapRef}
        className="absolute inset-0 cursor-grab active:cursor-grabbing"
        onMouseDown={startPan}
        onWheel={onWheel}
        onDoubleClick={addNoteAt}
      >
        <div className="absolute inset-0" style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`, transformOrigin: "0 0" }}>
          {/* Conexiones */}
          <svg className="absolute overflow-visible" style={{ left: 0, top: 0, width: 1, height: 1, pointerEvents: "none" }}>
            <defs>
              <marker id="arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6 Z" fill="#64748b" /></marker>
            </defs>
            {conexiones.map((c) => {
              const a = byId(c.desde_nota_id), b = byId(c.hasta_nota_id);
              if (!a || !b) return null;
              const x1 = a.x + a.ancho / 2, y1 = a.y + a.alto / 2, x2 = b.x + b.ancho / 2, y2 = b.y + b.alto / 2;
              return (
                <line
                  key={c.id} x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="#64748b" strokeWidth={2} markerEnd="url(#arrow)"
                  style={{ pointerEvents: "stroke", cursor: "pointer" }}
                  onClick={(e) => { e.stopPropagation(); if (confirm("¿Eliminar conexión?")) actions.deleteConnection.mutate(c.id); }}
                />
              );
            })}
          </svg>

          {/* Notas */}
          {notas.map((n) => (
            <NoteBox
              key={n.id}
              note={n}
              actions={actions}
              connectMode={connectMode}
              isConnectFrom={connectFrom === n.id}
              onMouseDown={(e) => startDrag(e, n)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function NoteBox({
  note, actions, connectMode, isConnectFrom, onMouseDown,
}: {
  note: CanvasNote;
  actions: ReturnType<typeof useCanvasActions>;
  connectMode: boolean;
  isConnectFrom: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [texto, setTexto] = useState(note.texto ?? "");

  return (
    <div
      className={`group absolute rounded-xl shadow-md p-2.5 text-sm text-slate-800 select-none ${connectMode ? "cursor-crosshair" : "cursor-grab active:cursor-grabbing"} ${isConnectFrom ? "ring-2 ring-brand-600" : ""}`}
      style={{ left: note.x, top: note.y, width: note.ancho, minHeight: note.alto, background: note.color }}
      onMouseDown={onMouseDown}
      onDoubleClick={(e) => { e.stopPropagation(); if (!connectMode) setEditing(true); }}
    >
      {editing ? (
        <div className="flex flex-col gap-1" onMouseDown={(e) => e.stopPropagation()}>
          <textarea
            className="w-full rounded border border-slate-300 bg-white/80 px-1.5 py-1 text-sm min-h-[60px]"
            value={texto} autoFocus
            onChange={(e) => setTexto(e.target.value)}
            onBlur={() => { setEditing(false); if (texto !== (note.texto ?? "")) actions.updateNote.mutate({ id: note.id, payload: { texto } }); }}
          />
          <div className="flex items-center gap-1 flex-wrap">
            {COLORS.map((c) => <button key={c} type="button" onClick={() => actions.updateNote.mutate({ id: note.id, payload: { color: c } })} className={`w-4 h-4 rounded-full border ${note.color === c ? "border-slate-800" : "border-transparent"}`} style={{ background: c }} />)}
          </div>
        </div>
      ) : (
        <>
          <div className="whitespace-pre-wrap break-words min-h-[40px]">{note.texto || <span className="text-slate-400 italic">Doble clic para escribir…</span>}</div>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => actions.deleteNote.mutate(note.id)}
            className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 grid place-items-center w-5 h-5 rounded-full bg-white border border-slate-200 text-rose-500 shadow"
            title="Eliminar nota"
          >
            <Trash2 size={11} />
          </button>
        </>
      )}
    </div>
  );
}
